// @ts-nocheck
import { dayjs } from 'util/shared/date';
import useLog from 'hooks/useLog';
import { getRiskFreeRate as _getRiskFreeRate, BondData } from 'sg-risk-engine';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { RISK_FREE_RATE } from 'util/BlackScholes';

const _cache: Record<string, Record<number, number>> = {};

const HOURS_PER_YEAR = 24 * 7 * 365;

const useRiskFreeRate = (targetDate: dayjs.Dayjs | string | number) => {
  const { fetchAPIWithLog } = useLog('useRiskFreeRate');
  const queryClient = useQueryClient();
  const { logError } = useLog('useRiskFreeRate');
  const [rateGetter, setRateGetter] = useState<
    ((tteYears: number) => number) | null
  >(null);
  const isFetchingRef = useRef(false);

  const ymd = dayjs(targetDate).format('YYYY-MM-DD');

  // Fetch rate when currentDate changes
  useEffect(() => {
    const fetchRate = async () => {
      if (isFetchingRef.current) {
        return;
      }
      isFetchingRef.current = true;

      _cache[ymd] = _cache[ymd] ?? {};

      const getBondEquivalentYields = async (): Promise<BondData> => {
        const data = await fetchAPIWithLog<BondData | { error: string }>(
          `v1/treasury_rates?date=${ymd}`,
        );
        if (data?.error != null) {
          logError(data.error, 'getBondEquivalentYields');

          // Return data as of 2025-10-03 as a best-effort fallback
          return new BondData(
            ymd,
            new Float64Array([
              30.0, 45.0, 60.0, 91.0, 121.0, 182.0, 365.0, 730.0, 1_095.0,
              1_825.0, 2_555.0, 3_650.0, 7_300.0, 10_950.0,
            ]),
            new Float64Array([
              0.041_7, 0.041_8, 0.041_2, 0.040_1, 0.039_5, 0.038, 0.036_2,
              0.035_5, 0.035_6, 0.036_8, 0.038_8, 0.041_2, 0.046_9, 0.047_2,
            ]),
          );
        }
        return data as BondData;
      };

      const { date, days, be_yields } = await queryClient.fetchQuery({
        queryKey: ['risk_free_rate', ymd],
        queryFn: getBondEquivalentYields,
        staleTime: 60 * 60 * 1_000, // 1 hour
      });

      const getter = function (tteYears: number) {
        // Round TTE to nearest hour for caching purposes
        const tteRounded =
          Math.round(tteYears * HOURS_PER_YEAR) / HOURS_PER_YEAR;
        const cached = _cache[ymd][tteRounded];
        if (cached != null) {
          return cached;
        }

        const bondData = new BondData(
          date,
          new Float64Array(days),
          new Float64Array(be_yields),
        );
        const rate = _getRiskFreeRate(bondData, tteRounded);
        _cache[ymd][tteRounded] = rate;
        return rate;
      };

      setRateGetter(() => getter);
      isFetchingRef.current = false;
    };

    void fetchRate();
  }, [ymd, fetchAPIWithLog, logError, queryClient]);

  const getRiskFreeRateForYte = useMemo(
    (): ((yte: number, returnNullIfNoRaterGetter?: boolean) => number) =>
      (yte: number, returnNullIfNoRaterGetter?: boolean): number => {
        if (rateGetter != null) {
          return rateGetter(yte);
        }
        if ((returnNullIfNoRaterGetter ?? false) == true) {
          return null;
        }
        return RISK_FREE_RATE;
      },
    [rateGetter],
  );

  return { getRiskFreeRateForYte };
};

export default useRiskFreeRate;
